/**
 * **범위 트리(매장 선택 창)의 판정·변환 한 곳** — 의존성이 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 문구는 [scopeTreeText.js](./scopeTreeText.js) 가 갖는다. 이 파일에 있는 것은 **서버 응답을 창이 읽는
 * 모양으로 바꾸는 순수 함수**와 경로다.
 *
 * ## 여기에 없는 것
 *
 * - **권한 판정** — 「이 계정이 무엇을 보는가」는 서버가 좁힌다(§1-3 의 같은 함수). 화면은 받은 것을 그린다.
 * - **조회** — 요청은 컴포넌트(경계)가 보낸다. 이 파일은 질의를 만들고 응답을 읽을 뿐이다.
 * - **매장 셀렉트의 조회 규약** — 그것은 [storeOptions.js](./storeOptions.js)(`GET /store/combo`)가 이미
 *   갖고 있고, 서버가 `/scope/**` 를 주지 않는 환경의 **폴백**이 그 셀렉트다. 두 축을 섞지 않는다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/B7b-후속2-사업장종류-샵인샵그룹-brief.md` §1-5.
 */
import { STORE_PICKER_TEXT } from './scopeTreeText.js'

/** 어드민 API base — 경로 리터럴을 컴포넌트마다 적지 않는다 */
export const SCOPE_API = '/scope/tree'

export const SCOPE_CUSTS_API = `${SCOPE_API}/custs`
export const SCOPE_STORES_API = `${SCOPE_API}/stores`
export const SCOPE_SEARCH_API = `${SCOPE_API}/search`
/** 선택 규칙 → 매장 집합(다중 선택). 규칙의 뜻은 서버와 화면이 **같은 시험 벡터**로 고정한다 */
export const SCOPE_RESOLVE_API = `${SCOPE_API}/resolve`

/**
 * 트리를 **부를 수 있는가**의 세션 액션 — 창의 첫 읽기(`GET /scope/tree/custs`)의 `sys_action`.
 *
 * 이 액션이 없는 세션(관리점·운영사 — R-E2 는 `/scope/tree/**` 를 주지 않았다)은 창을 열지 않고 **처음부터**
 * 셀렉트로 선다. 창을 열어 403 을 받고 떨어지는 길([scopeUnavailable])은 그 세션에게 없다: 보안 계층의 403 은
 * `logoutOn403:false` 와 무관하게 서버가 세션을 끊는다(`plugins/http/permittedRead` — compose 실측 2026-09-15).
 */
export const SCOPE_TREE_ACTION = 'ScopeTreeCusts'

/**
 * 사업장 종류 — 값은 둘뿐이고 「그 밖」은 **`null`** 이다(§1-1, 기존 행은 백필하지 않는다).
 * 모르는 값이 와도 배지를 만들지 않는 쪽이 옳다 — 화면이 서버가 하지 않은 말을 하지 않는다.
 */
export const CUST_TP = Object.freeze({ SHOP_GROUP: 'SHOP_GROUP' })

/**
 * 줄의 축 — 사업장은 펼 수 있고 매장은 잎이다. 나머지 셋은 **줄 자체가 상태**인 자리다:
 * 읽는 중·더 보기·비어 있음. 창이 `v-if` 세 개로 갈래를 그리는 대신 같은 배열의 한 줄로 온다.
 */
export const NODE_TYPE = Object.freeze({
  CUST: 'CUST',
  STORE: 'STORE',
  /** 「직접 소속 매장 (N)」 묶음 행 — 다중 선택 모드에서 `directOf` 규칙을 만드는 **보이는** 자리 */
  DIRECT: 'DIRECT',
  LOADING: 'LOADING',
  MORE: 'MORE',
  EMPTY: 'EMPTY',
})

/** 매장 한 장의 크기(§1-5 기본 50·상한 200). 창은 기본값만 쓴다 — 더 받는 자리는 「더 보기」다 */
export const STORE_PAGE_SIZE = 50

/** 검색 결과 상한(§1-5 기본 50·상한 100) */
export const SEARCH_LIMIT = 50

/** 서버가 400 으로 막는 질의어 길이 — 보내기 전에 화면이 먼저 답한다(§1-5) */
export const SEARCH_MIN_LENGTH = 2

/** 검색 디바운스(ms) — 글자마다 서버를 부르지 않는다 */
export const SEARCH_DEBOUNCE_MS = 250

/** 최근 고른 매장의 개수 — 창 맨 위에 서는 줄 수 */
export const RECENT_MAX = 5

/**
 * 조회의 axios config — **이 403 은 세션을 끝내지 않는다**(규약 §16.170 옵트아웃).
 *
 * `/scope/**` 의 URL 권한은 새 SQL 이 넣는다(§1-4). 그 SQL 이 아직 없는 환경에서 403 이 오면 이 앱의
 * 기본값은 세션을 지우고 로그인으로 하드 이동인데, 그러면 **매장을 고르려다 로그아웃**이 된다.
 * 여기서 대체 동작은 기존 셀렉트로 떨어지는 것이므로([scopeUnavailable]) 옵트아웃 조건을 만족한다.
 *
 * `showErrorMessage` 도 끈다 — 「이 서버는 트리를 모른다」는 이 창에서 **오류가 아니라 폴백의 신호**다.
 *
 * `showLoading` 도 끈다(CLAUDE.md 「로딩 스피너」 — 억제는 axios config 의 이 키 하나다). 창은 자기
 * 로딩을 이미 갖고 있다: 목록의 `v-loading` 은 **문**([readGate])이 소유하고 확정 버튼은 `:loading`
 * 이다. 전역 마스크까지 켜면 취소로 문이 풀려도 마스크가 **응답까지** 남아 트리 조작을 막는다 —
 * 버린 읽기를 기다리는 화면이 된다. `resolve` 도 같은 설정을 쓴다 — **창 안에서 누른 확정**은 그 진행을
 * 버튼이 말하고, 화면 진입의 기본 확정은 창이 닫힌 채 도는 일이라 애초에 말할 자리가 없다.
 *
 * **선택기가 연 읽기는 전부 이 하나를 쓴다** — 창의 네 조회·`resolve`, 그리고 창에서 고른 매장의
 * 셀렉트 한 줄(`storeOptionResolver`)까지. 세 키가 같은 값으로 두 곳에 적혀 있던 동안 한쪽만 고쳐질 자리였다.
 */
export const SILENT_READ_CONFIG = Object.freeze({ logoutOn403: false, showErrorMessage: false, showLoading: false })

// ─── 질의 ────────────────────────────────────────────────────────────────

/** 자식 사업장 질의 — `parent` 가 없으면 **범위의 뿌리**를 뜻한다(키 자체를 싣지 않는다) */
export function custsQuery(parentCustCd) {
  return parentCustCd == null ? {} : { parent: parentCustCd }
}

/** 한 사업장에 직접 달린 매장 질의 — 페이지는 0부터다(어드민 리스팅 관례) */
export function storesQuery(custCd, page, size) {
  return { custCd, page: page || 0, size: size || STORE_PAGE_SIZE }
}

/** 검색 질의 — 질의어는 다듬어 보낸다(앞뒤 공백은 조건이 아니다) */
export function searchQuery(q, limit) {
  return { q: trimmed(q), limit: limit || SEARCH_LIMIT }
}

/** 보낼 수 있는 질의어인가 — 두 자 미만은 **보내지 않는다**(서버가 400 이다) */
export function isSearchable(q) {
  return trimmed(q).length >= SEARCH_MIN_LENGTH
}

// ─── 응답 읽기 ───────────────────────────────────────────────────────────

/**
 * 사업장 노드 목록 — 봉투(`{items}`)로 와도 맨 배열로 와도 같은 값을 준다.
 *
 * 두 모양을 다 받는 이유: 같은 회차의 서버가 매장 쪽은 `{items, total}` 봉투로, 사업장 쪽은 노드 목록으로
 * 적혀 있다(§1-5 표). **믿는 자리를 하나로 줄이면** 어느 쪽이 오든 창이 흰 화면이 되지 않는다.
 */
export function custNodesOf(result) {
  return rowsOf(result)
    .map(custNodeOf)
    .filter((node) => node.custCd != null)
}

/**
 * 매장 한 장 — `{items, total, hasMore}`. 이상한 응답에도 값을 돌려준다.
 *
 * `hasMore` 는 서버가 말해 주면 **그 말을 쓴다**(§4-2). 안 주는 응답에서는 `null` 이고, 그때는 받은 수와
 * `total` 로 센다([hasMoreStores]) — 「모른다」와 「없다」를 같은 값으로 접지 않는다.
 */
export function storePageOf(result) {
  const row = result && typeof result === 'object' && !Array.isArray(result) ? result : {}
  const items = rowsOf(result)
    .map(storeNodeOf)
    .filter((node) => node.storeCd != null)

  return {
    items,
    total: typeof row.total === 'number' ? row.total : items.length,
    hasMore: typeof row.hasMore === 'boolean' ? row.hasMore : null,
  }
}

/**
 * 검색 응답 — `{stores, custs}`. 각 행은 **경로**(뿌리 → 부모)를 함께 갖는다(§1-5).
 *
 * 경로가 있어야 「같은 이름의 매장이 둘」일 때 사람이 무엇을 고르는지 안다. 경로가 없는 행은 경로 줄을
 * 만들지 않는다 — 지어내지 않는다.
 */
export function searchResultOf(result) {
  const row = result && typeof result === 'object' ? result : {}

  return {
    stores: toArray(row.stores)
      .map((store) => Object.assign(storeNodeOf(store), { path: pathOf(store.path) }))
      .filter((node) => node.storeCd != null),
    custs: toArray(row.custs)
      .map((cust) => Object.assign(custNodeOf(cust), { path: pathOf(cust.path) }))
      .filter((node) => node.custCd != null),
  }
}

/** 사업장 한 행 → 노드. 모르는 칸은 값이 없는 것으로 둔다(0 으로 지어내지 않는다) */
function custNodeOf(row) {
  const node = row && typeof row === 'object' ? row : {}

  return {
    type: NODE_TYPE.CUST,
    key: `C-${node.custCd}`,
    custCd: node.custCd != null ? node.custCd : null,
    custId: node.custId || '',
    custNm: node.custNm || '',
    custTp: node.custTp || null,
    useYn: node.useYn || 'Y',
    childCustCount: numberOrNull(node.childCustCount),
    directStoreCount: numberOrNull(node.directStoreCount),
    subtreeStoreCount: numberOrNull(node.subtreeStoreCount),
  }
}

/** 매장 한 행 → 잎 노드 */
function storeNodeOf(row) {
  const node = row && typeof row === 'object' ? row : {}

  return {
    type: NODE_TYPE.STORE,
    key: `S-${node.storeCd}`,
    storeCd: node.storeCd != null ? node.storeCd : null,
    // 이 매장이 **어느 사업장 아래인가** — 계보가 규칙 3상태를 정한다. 서버가 행마다 준다(§4-2).
    // 요청 파라미터(어느 사업장을 펼쳤나)는 검색 결과처럼 그것이 없는 응답의 폴백이다
    custCd: node.custCd != null ? node.custCd : null,
    storeId: node.storeId || '',
    storeNm: node.storeNm || '',
    busiNum: node.busiNum || '',
    useYn: node.useYn || 'Y',
    parentStoreCd: node.parentStoreCd != null ? node.parentStoreCd : null,
  }
}

function pathOf(path) {
  return toArray(path).map((step) => ({
    custCd: step && step.custCd != null ? step.custCd : null,
    custNm: (step && step.custNm) || '',
    custTp: (step && step.custTp) || null,
  }))
}

// ─── 창이 읽는 모양 ──────────────────────────────────────────────────────

/** 샵인샵 그룹 사업장인가 — 배지를 세우는 유일한 조건이다(§1-1) */
export function isShopGroup(node) {
  return !!node && node.custTp === CUST_TP.SHOP_GROUP
}

/** 비활성인가 — 사업장·매장이 같은 칸(`useYn`)을 쓴다 */
export function isInactive(node) {
  return !!node && node.useYn === 'N'
}

/**
 * 고를 수 있는 행인가 — **매장 잎**이다.
 *
 * 비활성 매장을 따로 막지 않는다: 이 가족의 매장 기준은 하나이고 **활성 매장만** 내려온다
 * (§1-5 「매장 기준」 2026-09-21 밤 결정 — 보이는 것 = 세는 것 = `resolve` 결과). 그래서 화면이
 * 거르거나 흐리게 그릴 매장이 없고, 흐린 것은 **폐업 사업장 노드**뿐이다(그 아래에도 활성 매장이 있다).
 */
export function isPickable(node) {
  return !!node && node.type === NODE_TYPE.STORE
}

/**
 * **체크 축을 갖는 줄인가** — 사업장·묶음 행·매장 셋이다.
 *
 * 같은 사실을 자리마다 `row.type === …` 로 다시 적으면(`ariaChecked`·`pickOf`) 새 줄 타입이 늘 때
 * 한쪽만 고치게 되고, 그러면 안내 줄이 스크린리더에 체크박스로 읽힌다. 축의 주인을 하나로 둔다.
 */
export function hasCheckAxis(node) {
  if (!node) return false

  return node.type === NODE_TYPE.CUST || node.type === NODE_TYPE.DIRECT || node.type === NODE_TYPE.STORE
}

/** 사업장 노드가 펼 것을 가졌는가 — 자식 사업장이나 직접 매장이 하나라도 있으면 잎이 아니다 */
export function hasChildren(node) {
  return !!node && node.type === NODE_TYPE.CUST && (positive(node.childCustCount) || positive(node.directStoreCount))
}

/** 매장 수 한 줄 — 「매장 3곳 · 전체 12곳」. 모르는 축은 말하지 않는다 */
export function storeCountText(node) {
  const parts = []
  if (positive(node && node.directStoreCount)) {
    parts.push(`${STORE_PICKER_TEXT.storeCountLabel} ${node.directStoreCount}${STORE_PICKER_TEXT.storeCountUnit}`)
  }
  if (positive(node && node.subtreeStoreCount) && node.subtreeStoreCount !== node.directStoreCount) {
    parts.push(`${STORE_PICKER_TEXT.subtreeCountLabel} ${node.subtreeStoreCount}${STORE_PICKER_TEXT.storeCountUnit}`)
  }

  return parts.join(' · ')
}

/** 경로 한 줄 — 「뿌리 › … › 부모」. 경로가 없으면 빈 문자열이라 줄 자체가 서지 않는다 */
export function pathText(path) {
  return toArray(path)
    .map((step) => (step && step.custNm) || '')
    .filter(Boolean)
    .join(' › ')
}

/**
 * 행들에서 **`storeCd` → 이름 사전**을 만든다 — 트리 한 장·검색 결과·최근 목록이 같은 모양이라 한 함수다.
 *
 * 사전은 **값의 그림자가 아니라 조회 캐시**다: 값은 언제나 `storeCd` 하나이고, 이 사전은 그 코드로
 * 이름을 묻는 자리일 뿐이라 값이 바뀌어도 버릴 것이 없다.
 */
export function storeNamesOf(rows) {
  return toArray(rows).reduce((names, row) => {
    if (!row || row.storeCd == null || !row.storeNm) return names

    return Object.assign(names, { [String(row.storeCd)]: row.storeNm })
  }, {})
}

/**
 * 고른 매장의 **이름 한 곳** — 단일 모드의 칸이 무엇을 보일지는 여기서만 정한다.
 *
 * 푸는 길이 셋이고 **순서가 곧 신뢰도**다:
 *
 * 1. **후보 목록**(`GET /store/combo`) — 부모가 값을 밖에서 채워 넣는 자리(수정 다이얼로그)도 이 길로 푼다
 * 2. **이름 사전** — 이번 세션에 트리·검색·최근에서 본 매장. 후보 목록이 **비어 있는 계정**(내부
 *    `admin`·`mng` 는 `/store/combo` 가 빈 배열인데 트리는 전 매장을 준다)과 후보 목록을 아예 받지 않는
 *    자리(정산 백필)가 여기서 답을 얻는다 — 그 자리가 드문 모서리가 아니라 **새로 연 정상 경로**다
 * 3. **코드 그대로** — 아무도 이름을 모를 때. 종전에도 매장 코드를 보여 주던 칸이 그 모습으로 남는다
 *
 * 서버에 이름을 다시 묻지 않는다: 고르는 순간 창이 이미 이름을 손에 들고 있다.
 */
export function storeLabelOf(value, options, names) {
  if (value == null || value === '') return ''

  const key = String(value)
  const row = toArray(options).find((item) => item && String(item.storeCd) === key)
  if (row) return storeRowLabel(row)

  const learned = names && typeof names === 'object' ? names[key] : ''

  return learned || key
}

/**
 * 후보 행 **하나**의 표시 이름 — 다국어 이름이 먼저다.
 *
 * [storeLabelOf] 의 후보 갈래와 폴백 셀렉트의 `el-option` 이 같이 쓴다. 옵션에만 `storeNmLan` 을
 * 적어 두면 그 칸을 주던 소비자(AI 챗봇의 `/api/chat/info` 는 `storeNm` 만 준다)에서 이름 자리에
 * **매장 코드**가 서고 이름으로 찾을 수도 없다.
 */
export function storeRowLabel(row) {
  if (!row) return ''

  return row.storeNmLan || row.storeNm || ''
}

/**
 * 매장 잎의 **보조 줄** — 매장 ID 와 사업자번호. 값이 없는 축은 가운뎃점만 남기지 않는다.
 *
 * 검색이 이 둘로도 찾으므로(§1-5) 무엇으로 걸렸는지 눈에 보여야 한다.
 */
export function storeSubText(node) {
  return [node && node.storeId, node && node.busiNum].filter(Boolean).join(' · ')
}

// ─── 보이는 줄 ───────────────────────────────────────────────────────────

/**
 * 트리 상태를 **평면 목록**으로 편다 — 창은 이 배열만 그린다. 순수 함수.
 *
 * el-tree 를 쓰지 않은 이유가 여기 있다: 검색 결과도 평면 목록이므로 **트리와 검색이 같은 모양**이 되고,
 * 키보드 이동·선택 규칙([moveCursor]·[isPickable])이 두 벌이 되지 않는다. 펼침·「더 보기」·「읽는 중」도
 * 분기가 아니라 **줄**이라, 그리는 쪽에 갈래가 생기지 않는다.
 *
 * 「직접 소속 매장 (N)」 묶음 행도 **모드와 무관하게 언제나** 선다: 다중 모드에서는 그 줄을 체크해
 * 「하부 전체가 아니라 직접 매장만」을 고르는 자리이고(§1-6 — 숨은 모드로 두지 않는다), 단일 모드에서는
 * 사업장의 직접 매장과 하위 사업장을 가르는 머리글이다. 고를 수 없는 줄은 [isPickable] 이 한 곳에서
 * 가리므로 단일 모드에서 이 줄은 커서가 지나가도 클릭해도 아무 일을 하지 않는다.
 *
 * @param {{roots: Array, children: object, expanded: object}} tree
 *   `children[key] = {custs, stores, total}` — 아직 안 읽었으면 키가 없다(읽는 중 줄이 선다)
 * @returns {Array} `{…노드, depth, expandable, expanded}`
 */
export function visibleRows(tree) {
  const state = tree && typeof tree === 'object' ? tree : {}
  const children = state.children && typeof state.children === 'object' ? state.children : {}
  const expanded = state.expanded && typeof state.expanded === 'object' ? state.expanded : {}
  const rows = []

  const walk = (nodes, depth) => {
    toArray(nodes).forEach((node) => {
      if (!node || !node.key) return
      const open = !!expanded[node.key]
      rows.push(Object.assign({}, node, { depth, expandable: hasChildren(node), expanded: open }))
      if (node.type !== NODE_TYPE.CUST || !open) return

      const loaded = children[node.key]
      if (!loaded) {
        rows.push({ type: NODE_TYPE.LOADING, key: `L-${node.key}`, depth: depth + 1 })

        return
      }

      walk(loaded.custs, depth + 1)

      // 묶음 행은 **자기가 머리글인 매장 바로 위**에 선다 — 자식 사업장 앞에 두면 머리글과 매장 사이에
      // 다른 사업장의 하부가 통째로 끼어든다
      storeRowsOf(node, loaded, depth + 1).forEach((row) => rows.push(row))

      if (toArray(loaded.custs).length === 0 && toArray(loaded.stores).length === 0) {
        rows.push({ type: NODE_TYPE.EMPTY, key: `E-${node.key}`, depth: depth + 1 })
      }
    })
  }

  walk(state.roots, 0)

  return rows
}

/**
 * 한 사업장의 **매장 줄들** — 「직접 소속 매장」 묶음 행 + 읽은 매장 한 장 + 「더 보기」. 순수 함수.
 *
 * 트리와 **검색으로 들어간 사업장의 매장 목록**이 같은 함수를 쓴다. 두 벌이던 동안 검색 쪽에는
 * 「더 보기」가 아예 없어서, 매장 737곳짜리 사업장을 검색으로 열면 51번째부터 볼 길이 없었다.
 * 장 번호·「더 보기」 유무·깊이의 주인이 하나이므로 한쪽만 고쳐지는 일이 없다.
 *
 * @param {{key: string, custCd: number, custNm: string, directStoreCount: ?number}} node 사업장 노드
 * @param {{stores: Array, total: ?number, hasMore: ?boolean}} loaded 읽은 매장 한 장
 * @param {number} depth 묶음 행이 설 깊이 — 매장·「더 보기」는 그 한 칸 아래다(매장이 없으면 같은 칸)
 */
export function storeRowsOf(node, loaded, depth) {
  const owner = node && typeof node === 'object' ? node : {}
  const page = loaded && typeof loaded === 'object' ? loaded : {}
  const stores = toArray(page.stores)
  const base = count(depth)
  const storeDepth = stores.length > 0 ? base + 1 : base
  const rows = []

  if (stores.length > 0) {
    rows.push({
      type: NODE_TYPE.DIRECT,
      key: `D-${owner.key}`,
      custCd: owner.custCd,
      custNm: owner.custNm,
      count: owner.directStoreCount != null ? owner.directStoreCount : page.total,
      depth: base,
    })
  }

  stores.forEach((store) => rows.push(Object.assign({}, store, { depth: storeDepth })))

  const more = page.hasMore != null ? page.hasMore : hasMoreStores(stores.length, page.total)
  if (more) {
    rows.push({
      type: NODE_TYPE.MORE,
      key: `M-${owner.key}`,
      custCd: owner.custCd,
      parentKey: owner.key,
      depth: storeDepth,
    })
  }

  return rows
}

/** 펼침 토글 — **새 객체**를 준다(상태를 제자리에서 고치지 않는다) */
export function toggleExpanded(expanded, key) {
  const rows = expanded && typeof expanded === 'object' ? expanded : {}
  const next = Object.assign({}, rows)
  if (next[key]) delete next[key]
  else next[key] = true

  return next
}

/**
 * 그 가지를 **닫는다** — 열려 있든 아니든 결과는 「닫힘」 하나다.
 *
 * [toggleExpanded] 와 뜻이 다르다: 읽기가 실패해 가지를 거두는 자리에서 뒤집기를 쓰면, 읽는 동안
 * 사람이 이미 접은 가지를 **다시 펴** 자식 없는 「읽는 중」만 남는다.
 */
export function withoutKey(expanded, key) {
  const rows = expanded && typeof expanded === 'object' ? expanded : {}
  const next = Object.assign({}, rows)
  delete next[key]

  return next
}

// ─── 더 보기 ─────────────────────────────────────────────────────────────

/** 다 읽었는가 — 받은 수가 전체와 같으면 「더 보기」를 만들지 않는다 */
export function hasMoreStores(loadedCount, total) {
  return count(loadedCount) < count(total)
}

/**
 * 다음 장 번호 — **읽은 장에 하나 더한다**.
 *
 * 받은 수를 장 크기로 나누는 셈법이 더 그럴듯해 보이지만 거짓이다: 서버가 한 장을 **덜 채워** 주면
 * (범위 밖 매장을 걸러낸 장) 그 셈은 같은 장을 다시 부르고 「더 보기」가 제자리를 맴돈다. 읽은 장을
 * 기억하는 쪽이 짧은 장에도 맞는다.
 */
export function nextStorePage(loadedPage) {
  return count(loadedPage) + 1
}

// ─── 읽기 키 ─────────────────────────────────────────────────────────────

/**
 * 창이 나가는 읽기의 **키 앞머리** — 앞머리가 곧 **취소 수명**이다([readGate] 의 `cancel(prefix)`).
 *
 * 축이 다섯이고 뜻이 다르다: 뿌리 · 사업장 자식 · 트리의 매장 장(펼침은 검색과 독립이라 안 버린다) ·
 * 검색(질의어가 바뀌면 버린다) · **검색으로 들어간 사업장의 매장**(그 상세를 떠나면 첫 장도 다음 장도
 * 함께 버린다). 리터럴을 자리마다 적으면 「더 보기」만 다른 앞머리를 갖게 되고, 그러면 상세를 떠난
 * 뒤에도 그 요청이 바쁨을 붙들고 실패하면 새 화면을 실패로 덮는다.
 *
 * **다섯이 서로 접두어가 아니어야 한다** — `cancel(prefix)` 는 키 공간 전체에 걸리는 규칙이라,
 * 한 축을 버리는 한 줄이 옆 축까지 걷으면 펼쳐 둔 가지가 영영 「읽는 중」이 된다. 단위 시험이 센다
 * (`custs:` 와 `custStores:` 는 글자 하나 차이라 우연에 기대지 않는다).
 */
export const SCOPE_READ_KEY = Object.freeze({
  roots: 'custs:',
  custChildren: 'cust:',
  treeStores: 'stores:',
  search: 'search:',
  searchStores: 'custStores:',
})

/** 검색 상세의 노드 키 — 트리(`C-`)와 앞머리를 달리해 같은 사업장을 양쪽에서 열어도 장이 안 섞인다 */
export const SEARCH_NODE_PREFIX = 'X-'

/** 검색으로 들어간 사업장의 노드 키 */
export function searchNodeKey(custCd) {
  return `${SEARCH_NODE_PREFIX}${custCd}`
}

/**
 * 매장 한 장을 읽는 **문 키** — 어느 수명에 속하는지는 `parentKey` 의 앞머리가 답한다.
 *
 * 장 번호를 키에 넣는 이유는 「더 보기」 연타로 같은 장을 두 번 받지 않기 위해서다(받으면 같은 매장이
 * 두 줄이 되고 장 번호가 멈춘다). 첫 장도 같은 규칙을 쓰므로 상세를 떠날 때 한 번의 취소가 둘 다 덮는다.
 *
 * **부모 키가 없으면 키를 만들지 않는다**(`null`). 어느 수명에 속하는지는 그 키가 답하는데, 없는 키를
 * 한쪽으로 떨어뜨리면 근거 없는 편이 계약이 된다 — 호출부가 그 전에 막는다.
 */
export function storePageReadKey(parentKey, page) {
  if (parentKey == null || parentKey === '') return null

  const key = String(parentKey)
  const prefix = key.indexOf(SEARCH_NODE_PREFIX) === 0 ? SCOPE_READ_KEY.searchStores : SCOPE_READ_KEY.treeStores

  return `${prefix}${key}:${count(page)}`
}

// ─── 선택 규칙 (다중 선택) ───────────────────────────────────────────────

/**
 * 규칙의 대상 셋 — 서버 `POST /scope/tree/resolve` 의 본문 키와 같은 문자열이다(§1-5).
 *
 * `custs` = 그 사업장 **아래 전체** · `directOf` = 그 사업장에 **직접 달린 매장만** · `stores` = 매장 하나.
 */
export const RULE_TARGET = Object.freeze({ CUSTS: 'custs', DIRECT_OF: 'directOf', STORES: 'stores' })

/** 선택 상태 셋 — 체크박스의 3상태 */
export const SELECTION = Object.freeze({ ON: 'ON', OFF: 'OFF', PARTIAL: 'PARTIAL' })

/** `resolve` 한 목록·합계 상한(§4-4). 넘으면 서버가 400 이라 화면이 먼저 말한다 */
export const RULE_LIST_MAX = 1000
export const RULE_TOTAL_MAX = 3000

/**
 * **빈 규칙** — 아무것도 고르지 않은 상태. 여섯 목록이 다 있다(서버가 생략을 빈 목록으로 읽지만,
 * 화면이 키의 유무로 갈리지 않게 언제나 같은 모양을 쓴다).
 */
export function emptyRules() {
  return { include: { custs: [], directOf: [], stores: [] }, exclude: { custs: [], directOf: [], stores: [] } }
}

/**
 * **한 매장이 뽑히는가** — 서버 해석기와 같은 뜻이다(§1-5 「선택 규칙」).
 *
 * 판정은 **가장 가까운 규칙이 이긴다**: 매장 → 그 사업장의 `directOf` → 그 사업장의 `custs` →
 * 부모의 `custs` → … 처음 만나는 규칙이 답이고, 아무것도 없으면 미선택. 같은 층에서 include 와
 * exclude 가 같이 있으면 **exclude 가 이긴다**.
 *
 * 두 리포가 같은 뜻을 구현하므로 브리프의 시험 벡터 ①~⑧ 이 양쪽에 그대로 있다.
 *
 * @param {object} rules [emptyRules] 모양
 * @param {number} storeCd
 * @param {Array<number>} custChain 그 매장의 사업장부터 뿌리까지(가까운 것부터)
 */
export function storeSelected(rules, storeCd, custChain) {
  const rule = rules || emptyRules()
  const chain = toArray(custChain)

  const hit = decide(rule, RULE_TARGET.STORES, storeCd)
  if (hit !== null) return hit

  if (chain.length > 0) {
    const direct = decide(rule, RULE_TARGET.DIRECT_OF, chain[0])
    if (direct !== null) return direct
  }

  for (let i = 0; i < chain.length; i += 1) {
    const inherited = decide(rule, RULE_TARGET.CUSTS, chain[i])
    if (inherited !== null) return inherited
  }

  return false
}

/**
 * 사업장 노드의 **3상태** — 자기 규칙과 조상에서 물려받은 답, 그리고 **자기 아래에 다른 규칙이
 * 있는가**로 정한다. 순수 함수.
 *
 * 아직 안 펼친 하부는 몰라도 된다: 규칙은 사람이 만든 것이고 사람이 만들 때 그 노드는 이미 불러와
 * 있었으므로, 「내 아래에 규칙이 있는가」는 **규칙 목록과 계보 색인**만으로 답할 수 있다.
 *
 * @param {object} rules
 * @param {number} custCd
 * @param {Array<number>} chain 자기 자신부터 뿌리까지(가까운 것부터, 자기 포함)
 * @param {{custParents: object, storeCust: object}} index 계보 색인
 */
export function custSelection(rules, custCd, chain, index) {
  const rule = rules || emptyRules()
  const own = decide(rule, RULE_TARGET.CUSTS, custCd)
  const inherited = own !== null ? own : inheritedOf(rule, toArray(chain).slice(1))
  const below = hasRuleBelow(rule, custCd, index)

  if (!below) return inherited ? SELECTION.ON : SELECTION.OFF

  return SELECTION.PARTIAL
}

/**
 * 「직접 소속 매장」 묶음 행의 **3상태** — 그 축의 규칙이 없으면 사업장에서 물려받고, **그 사업장에
 * 직접 달린 매장 중 답이 다른 것**이 하나라도 있으면 반선택이다. 순수 함수.
 *
 * 자기 `directOf` 규칙만 보던 동안 체크박스가 거짓을 말했다: 묶음을 켠 뒤 매장 하나를 빼도 ON 이고,
 * 매장 하나만 넣으면 OFF 였다. 그러면 「부분 선택을 전체 선택으로」 바꾸려는 클릭이 **전체 해제**가
 * 된다(ON 으로 읽혀서 끄는 쪽으로 간다). 사업장 노드가 이미 같은 규칙으로 [custSelection] 을 내므로
 * 묶음 행만 두 상태로 두면 같은 화면이 두 어휘를 쓴다.
 *
 * @param {{custParents: object, storeCust: object}} index 계보 색인 — 매장의 소속을 여기서 읽는다
 */
export function directSelection(rules, custCd, chain, index) {
  const rule = rules || emptyRules()
  const own = decide(rule, RULE_TARGET.DIRECT_OF, custCd)
  const base = own !== null ? own : inheritedOf(rule, toArray(chain))
  if (hasDirectStoreException(rule, custCd, base, index)) return SELECTION.PARTIAL

  return base ? SELECTION.ON : SELECTION.OFF
}

/** 그 사업장에 **직접 달린 매장**의 규칙 중 묶음의 답과 다른 것이 있는가 */
function hasDirectStoreException(rules, custCd, base, index) {
  const owners = (index && index.storeCust) || {}

  return toArray(rules.include.stores)
    .concat(toArray(rules.exclude.stores))
    .some((storeCd) => owners[storeCd] === custCd && decide(rules, RULE_TARGET.STORES, storeCd) !== base)
}

/**
 * 규칙 하나를 더하거나 뺀 **새 규칙** — 언제나 **최소 규칙 집합**으로 정규화한다.
 *
 * 두 가지를 한다: ① 이 대상 **아래의 규칙을 전부 지운다**(새 규칙이 그것들을 덮는다) ② 새 규칙이
 * 물려받는 답과 **같으면 아예 넣지 않는다**(「전체 선택 뒤 하나를 다시 선택」이 규칙을 늘리지 않는다).
 *
 * 그래서 상한(§4-4 한 목록 1,000)에 닿는 일이 드물고, 사람이 만든 규칙이 곧 화면에 보이는 칩이 된다.
 *
 * 계보는 **색인에서 유도한다**([ruleChain]) — 호출부가 주던 것을 거뒀다. 정규화가 쓰는 계보와 갈리는
 * 날 방금 더한 규칙이 조용히 사라지기 때문이다: 여기서는 「상속과 다르다」고 넣었는데 [normalizeRules]
 * 가 다른 계보로 「같다」고 보고 지우는 모양이다. 순서 규칙의 주인은 하나다.
 *
 * @param {object} rules
 * @param {{target: string, id: number}} pick 무엇을 눌렀나
 * @param {boolean} next 켜는가
 * @param {object} index 계보 색인
 */
export function withRule(rules, pick, next, index) {
  const rule = rules || emptyRules()
  const target = pick && pick.target
  const id = pick && pick.id
  if (!target || id == null) return copyRules(rule)

  const cleared = clearBelow(rule, target, id, index)
  const inherited = inheritedFor(cleared, target, id, ruleChain(target, id, index))

  // 물려받은 답과 같으면 규칙을 만들지 않는다 — 그것이 최소 집합이다
  if (inherited === next) return normalizeRules(cleared, index)

  const side = next ? 'include' : 'exclude'

  return normalizeRules(
    Object.assign({}, cleared, {
      [side]: Object.assign({}, cleared[side], { [target]: cleared[side][target].concat([id]) }),
    }),
    index
  )
}

/**
 * 칩의 × — 그 규칙 하나만 지운 **새 규칙**. 지운 뒤에도 [normalizeRules] 를 지난다.
 *
 * 규칙 하나를 지우면 **다른 규칙이 불필요해질 수 있다**: `A 전체 · B 제외 · b1 포함`(b1 ∈ B)에서 B
 * 제외를 지우면 b1 은 A 에서 물려받는 답과 같아진다. 그 규칙을 남기면 칩이 하나 더 보이고
 * [selectedCount] 가 b1 을 두 번 세어 「실제 3곳인데 4곳」이 된다 — 최소 집합 불변식이 [withRule]
 * 에서만 지켜지고 여기서 깨지던 자리다.
 */
export function withoutRule(rules, side, target, id, index) {
  const rule = rules || emptyRules()
  if (side !== 'include' && side !== 'exclude') return copyRules(rule)

  return normalizeRules(dropRule(rule, side, target, id), index)
}

/** 그 규칙 하나를 뺀 사본 — 정규화는 하지 않는다(정규화가 이 함수를 쓴다) */
function dropRule(rules, side, target, id) {
  const next = copyRules(rules)
  next[side][target] = next[side][target].filter((value) => value !== id)

  return next
}

/**
 * **최소 규칙 집합으로 접는다** — 물려받는 답과 같은 규칙을 지운 새 규칙. 순수 함수.
 *
 * 규칙을 더하는 쪽([withRule])과 빼는 쪽([withoutRule])이 **같은 이 함수**를 지난다. 한쪽에만
 * 두면 「빼기」가 만든 군더더기를 「더하기」가 볼 때까지 화면이 거짓 합계를 말한다.
 *
 * 한 번의 패스로 충분하다: 불필요한 규칙을 지우는 일은 **다른 규칙의 상속 답을 바꾸지 않는다**
 * (지워지는 규칙은 정의상 상속 답과 같으므로 그 아래의 판정도 그대로다). 그래서 판정은 언제나
 * **들어온 집합**을 기준으로 하고, 지우는 중간 상태를 다시 읽지 않는다.
 *
 * **색인에 없는 id 의 규칙은 지우지 않는다** — 계보를 모르면 상속 답도 모른다. 모르는 것을 근거로
 * 사람이 만든 선택을 지우지 않는다([selectionChips] 가 이름 모르는 규칙을 버리지 않는 것과 같다).
 */
export function normalizeRules(rules, index) {
  const base = copyRules(rules)
  const next = copyRules(rules)

  ;['include', 'exclude'].forEach((side) => {
    Object.keys(RULE_TARGET).forEach((key) => {
      const target = RULE_TARGET[key]
      next[side][target] = base[side][target].filter((id) => !redundantRule(base, side, target, id, index))
    })
  })

  return next
}

/** 이 규칙이 **없어도 같은 답**인가 — 색인이 계보를 모르면 언제나 「아니오」다 */
function redundantRule(rules, side, target, id, index) {
  const chain = ruleChain(target, id, index)
  if (chain == null) return false

  return inheritedFor(dropRule(rules, side, target, id), target, id, chain) === (side === 'include')
}

/** 규칙 대상의 계보 — 사업장은 자기부터, 매장은 **소속 사업장**부터. 모르는 대상은 `null` */
function ruleChain(target, id, index) {
  if (target === RULE_TARGET.STORES) {
    const owner = index && index.storeCust ? index.storeCust[id] : null

    return owner == null ? null : chainOf(index, owner)
  }

  return knownCust(index, id) ? chainOf(index, id) : null
}

/** 이 사업장을 색인이 아는가 — 계보·이름·매장 수 중 하나라도 있으면 안다(뿌리는 부모가 없다) */
function knownCust(index, custCd) {
  const row = index || {}

  return (
    (row.custParents && row.custParents[custCd] != null) ||
    (row.names && row.names[custCd] != null) ||
    (row.counts && row.counts[custCd] != null)
  )
}

/**
 * 그 사업장부터 **뿌리까지** — 규칙의 「가장 가까운 것부터」가 이 순서다. 순수 함수.
 *
 * 창의 체크 상태·규칙 만들기·정규화가 전부 이 순서를 쓴다. 컴포넌트가 같은 루프를 따로 갖고 있으면
 * 순서 규칙이 두 곳에 살고, 계보를 고치는 날 한쪽만 고쳐진다. 색인이 순환이어도 멈춘다(64 단계).
 */
export function chainOf(index, custCd) {
  const parents = (index && index.custParents) || {}
  const chain = []
  let cursor = custCd
  let guard = 0

  while (cursor != null && guard < 64) {
    chain.push(cursor)
    cursor = parents[cursor]
    guard += 1
  }

  return chain
}

/**
 * 선택 요약 **칩** — 사람이 만든 규칙이 그대로 한 줄씩이다(「피버트 전체 737」·「바로고 동해 제외 −221」).
 *
 * 이름·수를 모르는 규칙(색인에 없는 id)도 **버리지 않는다** — 코드만으로 그린다. 규칙은 사실이고,
 * 이름을 모른다는 이유로 사람이 만든 선택을 화면에서 지우면 무엇을 고쳤는지 알 수 없다.
 */
export function selectionChips(rules, index) {
  const rule = rules || emptyRules()
  const rows = []

  ;['include', 'exclude'].forEach((side) => {
    Object.keys(RULE_TARGET).forEach((key) => {
      const target = RULE_TARGET[key]
      toArray(rule[side][target]).forEach((id) => {
        rows.push({
          key: `${side}-${target}-${id}`,
          side,
          target,
          id,
          label: ruleLabel(target, id, index),
          count: ruleWeight(target, id, index),
          exclude: side === 'exclude',
        })
      })
    })
  })

  return rows
}

/**
 * 고른 매장 **수** — 불러온 노드의 매장 수와 규칙만으로 센다(서버 왕복 없이).
 *
 * 최소 규칙 집합이라 include 는 서로 겹치지 않고 exclude 는 언제나 어떤 include 아래에 있다.
 * 그래서 **더하고 빼는 것으로 충분하다**. 수를 모르는 규칙이 하나라도 있으면 `null` 이다 —
 * 모르면서 숫자를 지어내지 않는다(확정 때 서버 `total` 이 정본이다).
 */
export function selectedCount(rules, index) {
  const rule = rules || emptyRules()
  let total = 0

  for (const side of ['include', 'exclude']) {
    for (const key of Object.keys(RULE_TARGET)) {
      const target = RULE_TARGET[key]
      for (const id of toArray(rule[side][target])) {
        const weight = ruleWeight(target, id, index)
        if (weight == null) return null
        total += side === 'include' ? weight : -weight
      }
    }
  }

  return total < 0 ? 0 : total
}

/** 규칙이 하나라도 있는가 — 확정 버튼과 「전체 해제」가 묻는 것 */
export function hasRules(rules) {
  return selectionChips(rules, null).length > 0
}

/** `resolve` 요청 본문 — 여섯 목록을 언제나 싣는다(사본이라 호출부가 고쳐도 규칙이 안 바뀐다) */
export function resolveBody(rules) {
  return copyRules(rules || emptyRules())
}

/** 서버 상한을 넘겼는가 — 400 을 받기 전에 화면이 먼저 말할 수 있다(§4-4) */
export function rulesOverLimit(rules) {
  const rule = rules || emptyRules()
  let total = 0
  let over = false

  ;['include', 'exclude'].forEach((side) => {
    Object.keys(RULE_TARGET).forEach((key) => {
      const size = toArray(rule[side][RULE_TARGET[key]]).length
      total += size
      if (size > RULE_LIST_MAX) over = true
    })
  })

  return over || total > RULE_TOTAL_MAX
}

/** `resolve` 응답 — `{storeCds, total}`. 이상한 응답에도 값을 돌려준다 */
export function resolveResultOf(result) {
  const row = result && typeof result === 'object' ? result : {}
  const storeCds = toArray(row.storeCds).filter((cd) => cd != null)

  return { storeCds, total: typeof row.total === 'number' ? row.total : storeCds.length }
}

/**
 * 부모 화면에 주는 값 — **지금 통계 화면이 보내던 모양 그대로**(`', '` join 문자열).
 * 조회 로직을 건드리지 않는다는 계약이 이 한 줄에 있다.
 */
export function storeCdsValue(storeCds) {
  return toArray(storeCds).join(', ')
}

// ─── 규칙의 속살 ─────────────────────────────────────────────────────────

/** 이 대상에 규칙이 있는가 — `true`(include) · `false`(exclude) · `null`(없음). exclude 가 이긴다 */
function decide(rules, target, id) {
  if (toArray(rules.exclude[target]).indexOf(id) > -1) return false
  if (toArray(rules.include[target]).indexOf(id) > -1) return true

  return null
}

/** 조상들의 `custs` 규칙에서 물려받은 답 */
function inheritedOf(rules, chain) {
  for (const custCd of toArray(chain)) {
    const answer = decide(rules, RULE_TARGET.CUSTS, custCd)
    if (answer !== null) return answer
  }

  return false
}

/** 이 대상이 **자기 규칙 없이** 물려받을 답 */
function inheritedFor(rules, target, id, chain) {
  const rows = toArray(chain)
  if (target === RULE_TARGET.CUSTS) return inheritedOf(rules, rows.slice(1))
  if (target === RULE_TARGET.DIRECT_OF) return inheritedOf(rules, rows)

  // 매장: 자기 사업장의 `directOf` 가 먼저다
  const direct = rows.length > 0 ? decide(rules, RULE_TARGET.DIRECT_OF, rows[0]) : null

  return direct !== null ? direct : inheritedOf(rules, rows)
}

/** 이 대상 **아래**의 규칙을 전부 지운다 — 새 규칙이 그것들을 덮는다 */
function clearBelow(rules, target, id, index) {
  const next = copyRules(rules)

  ;['include', 'exclude'].forEach((side) => {
    next[side].custs = next[side].custs.filter(
      (cd) => !(cd === id && target === RULE_TARGET.CUSTS) && !under(cd, target, id, index, 'cust')
    )
    next[side].directOf = next[side].directOf.filter(
      (cd) => !(cd === id && target !== RULE_TARGET.STORES) && !under(cd, target, id, index, 'cust')
    )
    next[side].stores = next[side].stores.filter((storeCd) => {
      if (target === RULE_TARGET.STORES) return storeCd !== id
      const custCd = index && index.storeCust ? index.storeCust[storeCd] : null
      if (custCd == null) return true
      if (target === RULE_TARGET.DIRECT_OF) return custCd !== id

      return custCd !== id && !isDescendant(index, custCd, id)
    })
  })

  return next
}

/** 규칙의 대상(사업장)이 이 대상 아래인가 */
function under(candidate, target, id, index, kind) {
  if (kind !== 'cust' || target === RULE_TARGET.STORES) return false
  if (target === RULE_TARGET.DIRECT_OF) return false

  return isDescendant(index, candidate, id)
}

/** 내 아래에 다른 규칙이 하나라도 있는가 — 3상태의 「반선택」이 여기서 나온다 */
function hasRuleBelow(rules, custCd, index) {
  const sides = ['include', 'exclude']

  for (const side of sides) {
    for (const cd of toArray(rules[side].custs)) {
      if (cd !== custCd && isDescendant(index, cd, custCd)) return true
    }
    for (const cd of toArray(rules[side].directOf)) {
      if (cd === custCd || isDescendant(index, cd, custCd)) return true
    }
    for (const storeCd of toArray(rules[side].stores)) {
      const owner = index && index.storeCust ? index.storeCust[storeCd] : null
      if (owner != null && (owner === custCd || isDescendant(index, owner, custCd))) return true
    }
  }

  return false
}

/** 계보 색인으로 후손 여부 — 색인에 없으면 **아니오**(모르는 것을 후손이라 하지 않는다) */
function isDescendant(index, candidate, ancestorCustCd) {
  const parents = (index && index.custParents) || {}
  let cursor = parents[candidate]
  let guard = 0

  while (cursor != null && guard < 64) {
    if (cursor === ancestorCustCd) return true
    cursor = parents[cursor]
    guard += 1
  }

  return false
}

function ruleLabel(target, id, index) {
  const names = (index && index.names) || {}
  const storeNames = (index && index.storeNames) || {}
  if (target === RULE_TARGET.STORES) return storeNames[id] || `#${id}`

  return names[id] || `#${id}`
}

/** 규칙 하나가 세는 매장 수 — 모르면 `null` */
function ruleWeight(target, id, index) {
  if (target === RULE_TARGET.STORES) return 1
  const counts = (index && index.counts) || {}
  const row = counts[id]
  if (!row) return null
  const value = target === RULE_TARGET.CUSTS ? row.subtree : row.direct

  return typeof value === 'number' ? value : null
}

/**
 * 규칙의 **사본** — 창이 「확정본」과 「편집본」을 나눠 들 때 그 복사 규칙의 주인이다.
 *
 * 여섯 목록이 전부 새 배열이라 한쪽을 고쳐도 다른 쪽이 안 바뀐다. 창에서 `Object.assign` 로 얕게
 * 베끼면 목록 배열을 공유해 **취소한 편집이 확정본에 섞인다**.
 */
export function copyRules(rules) {
  const row = rules || emptyRules()

  return {
    include: {
      custs: toArray(row.include && row.include.custs).slice(),
      directOf: toArray(row.include && row.include.directOf).slice(),
      stores: toArray(row.include && row.include.stores).slice(),
    },
    exclude: {
      custs: toArray(row.exclude && row.exclude.custs).slice(),
      directOf: toArray(row.exclude && row.exclude.directOf).slice(),
      stores: toArray(row.exclude && row.exclude.stores).slice(),
    },
  }
}

// ─── 폴백 ────────────────────────────────────────────────────────────────

/**
 * 이 실패가 **「이 서버는 트리를 모른다」인가** — 그러면 창을 열지 않고 기존 셀렉트로 떨어진다(§1-6).
 *
 * 404(경로 없음)·403(URL 권한 행 없음) 둘뿐이다. 500·전송 실패는 폴백이 아니라 **실패**다 —
 * 그것까지 폴백으로 접으면 서버가 잠깐 아픈 날 화면이 조용히 옛 동작으로 돌아가고 아무도 모른다.
 *
 * @param {?{status: ?number}} failure `HttpError` 또는 `null`
 */
export function scopeUnavailable(failure) {
  const status = failure && failure.status
  return status === 404 || status === 403
}

// ─── 최근 고른 매장 ──────────────────────────────────────────────────────

/**
 * 최근 목록에 한 매장을 얹은 **새 배열** — 맨 앞이 방금 고른 것이고, 같은 매장은 한 번만 남는다.
 *
 * 원본을 바꾸지 않는다(불변). 상한을 넘으면 오래된 것부터 버린다.
 */
export function recentAfterPick(recent, store, max) {
  if (!store || store.storeCd == null) return toArray(recent).slice()
  const limit = max > 0 ? max : RECENT_MAX

  return [store].concat(toArray(recent).filter((row) => row && row.storeCd !== store.storeCd)).slice(0, limit)
}

/**
 * 최근 목록의 저장 키 — **계정마다 따로**.
 *
 * 한 브라우저에서 계정을 갈아 가며 쓰는 일이 있고(내부 운영자), 키가 하나면 앞 계정이 고른 매장이
 * 다음 계정의 창에 뜬다 — 그 계정은 그 매장을 볼 수 없을 수도 있다. 계정 ID 가 없으면 `null` 이라
 * 호출부가 저장을 건너뛴다(익명 상태에서 남길 것이 없다).
 */
export function recentStorageKey(accountId) {
  const id = trimmed(accountId)
  return id ? `store_picker_recent_${id}` : null
}

/** 저장된 값 → 최근 목록. 모양이 틀리면 빈 목록이다(깨진 값 하나가 창을 막지 않는다) */
export function recentOf(raw) {
  if (!raw) return []
  try {
    return toArray(JSON.parse(raw))
      .map(storeNodeOf)
      .filter((node) => node.storeCd != null)
      .slice(0, RECENT_MAX)
  } catch (error) {
    return []
  }
}

// ─── 키보드 ──────────────────────────────────────────────────────────────

/**
 * ↑↓ 로 옮긴 **다음 자리** — **모든 줄을 지난다**(사업장·묶음 행·「더 보기」도).
 *
 * 커서는 **트리의 커서이지 선택이 아니다**: 무엇을 고를 수 있는지는 Enter 자리에서 [isPickable] 이
 * 따로 답하고(고를 수 없는 줄에서 Enter 는 아무 일도 하지 않는다), 무엇을 체크할 수 있는지는 다중
 * 모드의 Space 가 답한다. 한때 고를 수 있는 줄만 지났는데, 그러면 **사업장 줄에 설 수 없어 ←·→ 로
 * 펼칠 수가 없다** — 키보드만 쓰는 사람은 하부의 매장에 영영 닿지 못한다. 모드마다 다른 규칙을 두면
 * 그 구멍이 한쪽에만 남으므로 커서는 한 벌이다.
 *
 * 목록이 비었으면 `-1`(고른 것 없음)이다.
 *
 * @param {Array} rows 지금 보이는 평면 목록
 * @param {number} index 지금 자리(없으면 -1)
 * @param {number} delta `1` 아래 · `-1` 위
 */
export function moveCursor(rows, index, delta) {
  const list = toArray(rows)
  if (list.length === 0) return -1

  const step = delta < 0 ? -1 : 1
  let next = typeof index === 'number' && index >= 0 ? index + step : step > 0 ? 0 : list.length - 1
  if (next < 0) next = list.length - 1
  if (next >= list.length) next = 0

  return next
}

// ─── 작은 것들 ───────────────────────────────────────────────────────────

function rowsOf(result) {
  if (Array.isArray(result)) return result
  const row = result && typeof result === 'object' ? result : {}

  return toArray(row.items)
}

function toArray(value) {
  return Array.isArray(value) ? value : []
}

function numberOrNull(value) {
  return typeof value === 'number' && isFinite(value) ? value : null
}

function positive(value) {
  return typeof value === 'number' && value > 0
}

function count(value) {
  return typeof value === 'number' && value > 0 ? value : 0
}

function trimmed(value) {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim()
}
